import { z } from 'zod';
import { METODOS_PAGO } from '../tipos.ts';
import { autorizacionesAdjuntas } from './autorizaciones.ts';
import { decimalNoNegativo, decimalPositivo, paginacion, uuid } from './comun.ts';

// ─── Retiros parciales de efectivo (bloque 8, 2026-10-09) ───────────────────
/** El cajero saca efectivo de su caja abierta y dice a quién se lo entrega; la dueña lo confirma desde su usuario. */
export const retiroInput = z.object({
  moneda: z.enum(['USD', 'BS']),
  monto: decimalPositivo,
  /** A quién o a qué caja se entrega el efectivo. */
  recibe: z.string().trim().min(3, 'Indica a quién se le entrega').max(120),
  nota: z.string().trim().max(300).optional(),
  /** Retiro de la caja de otro cajero (solo quien autoriza cierres); sin indicar = mi caja. */
  sesion_id: uuid.optional(),
});
export type RetiroInput = z.infer<typeof retiroInput>;

/** La dueña confirma que recibió el dinero, o lo deja observado diciendo qué no cuadra. */
export const retiroConfirmarInput = z
  .object({ observado: z.boolean().default(false), comentario: z.string().trim().max(300).optional() })
  .refine((d) => !d.observado || (d.comentario ?? '').length >= 3, { message: 'Indica qué no cuadra', path: ['comentario'] });
export type RetiroConfirmarInput = z.infer<typeof retiroConfirmarInput>;
/** Lista de retiros: «Desde / Hasta» (día de registro, hora de Caracas) y páginas de 25 (hallazgo T19). */
export const retirosFiltros = paginacion.extend({ estado: z.enum(['por_confirmar', 'confirmado', 'observado']).optional(), sesion_id: uuid.optional(), desde: z.iso.date().optional(), hasta: z.iso.date().optional() });

export interface RetiroDTO {
  id: string;
  numero_formateado: string;
  sucursal_nombre: string;
  sesion_id: string;
  caja_nombre: string;
  cajero_nombre: string;
  moneda: 'USD' | 'BS';
  monto: string;
  monto_usd: string;
  recibe: string;
  nota: string | null;
  estado: 'por_confirmar' | 'confirmado' | 'observado';
  registrado_por_nombre: string;
  registrado_en: string;
  confirmado_por_nombre: string | null;
  confirmado_en: string | null;
  comentario: string | null;
}

// ─── Conteo a ciegas y cierre en dos pasos ──────────────────────────────────
const sinRepetir = (ls: { metodo: string }[]) => new Set(ls.map((l) => l.metodo)).size === ls.length;
export const conteoLineaInput = z.object({ metodo: z.enum(METODOS_PAGO), monto: decimalNoNegativo });
export const lotePuntoInput = z.object({ numero: z.string().trim().min(1, 'Número de lote').max(30), monto: decimalNoNegativo });

/**
 * Paso 1 del cierre: el cajero declara lo que contó en cada método, sin ver lo que espera el sistema. Se guarda tal
 * cual; después se muestran las diferencias. El punto de venta se declara por lote (número y monto).
 */
export const declararConteoInput = z.object({
  /** Lo contado por método, en la moneda del método. El efectivo va CON el fondo incluido. */
  conteo: z.array(conteoLineaInput).max(20).refine(sinRepetir, 'Cada método se declara una sola vez'),
  lotes_punto: z.array(lotePuntoInput).max(20).default([]),
  observacion: z.string().trim().max(500).optional(),
});
export type DeclararConteoInput = z.infer<typeof declararConteoInput>;

/**
 * Paso 2 (solo si hubo diferencia): justificación y, por encima del umbral, autorización. Sin observación: la del cierre
 * es la del conteo (paso 1) y el paso 2 la reemplazaba (hallazgo T25); una «observación» que llegue aquí se ignora.
 */
export const cerrarConDiferenciaInput = z.object({
  justificacion_diferencia: z.string().trim().max(500).optional(),
  autorizaciones: autorizacionesAdjuntas,
});
export type CerrarConDiferenciaInput = z.infer<typeof cerrarConDiferenciaInput>;

export const reconteoInput = z.object({ motivo: z.string().trim().min(5, 'Indica por qué se vuelve a contar').max(300) });

/** Lo que el cajero ve antes de declarar: qué métodos se cuentan y el fondo de la caja. Sin montos esperados. */
export interface ConteoCajaDTO {
  sesion_id: string;
  caja_nombre: string;
  usuario_nombre: string;
  abierta_en: string;
  turno: number;
  estado: 'abierta' | 'cerrada';
  /** Ya declaró: el cierre muestra las diferencias. */
  declarado: boolean;
  fondo_usd: string;
  fondo_bs: string;
  metodos: { metodo: string; moneda: 'USD' | 'BS'; fondo: string }[];
  punto_banco: string | null;
  declara_bancarios: boolean;
  retiros: RetiroDTO[];
  esperas_pendientes: number;
}
